/**
 * ALTIORA · ODE · Opportunity Discovery Engine (Sprint 5)
 *
 * Un unique tableau de bord matinal qui répond aux 6 questions :
 * Où · Qui · Pourquoi · Quel produit · Dans quel ordre · Combien.
 */
import { useEffect, useState, useCallback } from "react";
import {
  Sparkles, Flame, Users, MapPin, Zap, AlertTriangle, RefreshCw,
  Plus, Radar as RadarIcon, ArrowRight, TrendingUp, X,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  PageShell, PageHeader, Card, Section, PrimaryButton, GhostButton,
  DangerButton, Modal, FormField, Input, Textarea, Select, Loader,
  EmptyState, Badge, Metric, Alert,
} from "@/components/altiora/PremiumUI";


const KIND_LABEL = {
  networking: "Réseautage", economic: "Économique",
  partner: "Partenaire",     territory: "Territoire",
  ecosystem: "Écosystème",
};

const CHANNEL_LABEL = {
  phone: "Appel", email: "Courriel", meeting: "Rencontre",
  event: "Événement", partner: "Via partenaire", linkedin: "LinkedIn",
};

const URGENCY_VARIANT = {
  critical: "red", high: "gold", medium: "navy", low: "info",
};

const money = (n) => (n || 0).toLocaleString("fr-CA", {
  style: "currency", currency: "CAD", maximumFractionDigits: 0,
});


export default function ODE() {
  const [dash, setDash] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/ode/dashboard");
      setDash(data);
    } catch { toast.error("Chargement du radar impossible"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <PageShell testid="ode-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <PageHeader
          eyebrow="Sprint 5 · Copilote économique"
          title="Radar des opportunités"
          subtitle="Où · Qui · Pourquoi · Quel produit · Dans quel ordre · Combien. Chaque matin, votre IA vous propose les meilleures actions du jour."
          testid="ode-header"
          actions={
            <>
              <GhostButton onClick={load} testid="ode-refresh">
                <RefreshCw size={13} /> Rafraîchir
              </GhostButton>
              <GhostButton onClick={async () => {
                toast.loading("Scan des radars en cours…", { id: "ode-scan" });
                try {
                  const { data } = await api.post("/ode/radars/scan");
                  const total = Object.values(data)
                    .reduce((s, r) => s + (r.created || 0), 0);
                  toast.success(`Scan terminé · ${total} nouvelles opportunités`,
                                    { id: "ode-scan" });
                  load();
                } catch { toast.error("Scan impossible", { id: "ode-scan" }); }
              }} testid="ode-scan-now">
                <RadarIcon size={13} /> Scanner maintenant
              </GhostButton>
              <PrimaryButton onClick={() => setShowCreate(true)} testid="ode-new">
                <Plus size={13} /> Ajouter une opportunité
              </PrimaryButton>
            </>
          }
        />

        {loading && <div className="py-16 flex justify-center"><Loader /></div>}

        {!loading && dash && (
          <>
            {/* Summary strip */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
              <Metric label="Opportunités actives" value={dash.summary.active_count}
                      testid="ode-metric-active" />
              <Metric label="Revenu potentiel"
                      value={money(dash.summary.revenue_outlook)}
                      accent="#C5A880" testid="ode-metric-revenue" />
              <Metric label="Réseautage"
                      value={dash.summary.by_kind?.networking || 0}
                      testid="ode-metric-networking" />
              <Metric label="Écosystème"
                      value={dash.summary.by_kind?.ecosystem || 0}
                      accent="#16A34A" testid="ode-metric-ecosystem" />
            </div>

            {/* TOP 5 TODAY */}
            <Card className="p-4 mb-5" testid="ode-top-card">
              <Section icon={Flame} title="🔥 Les 5 meilleures opportunités aujourd'hui"
                       hint="Triées par score IA — chaque carte répond aux 6 questions">
                {dash.top_today.length === 0 ? (
                  <EmptyState icon={RadarIcon} title="Aucune opportunité pour l'instant"
                              description="Ajoutez-en une pour lancer le moteur décisionnel."
                              testid="ode-top-empty" />
                ) : (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    {dash.top_today.map((o) => (
                      <OpportunityRow key={o.id} opp={o}
                                        onOpen={() => setDetail(o)} />
                    ))}
                  </div>
                )}
              </Section>
            </Card>

            {/* Next Best Actions */}
            <Card className="p-4 mb-5" testid="ode-actions-card">
              <Section icon={Zap} title="🎯 5 prochaines meilleures actions">
                {dash.next_best_actions.length === 0 ? (
                  <div className="text-[12px] italic" style={{ color: "#94A3B8" }}>
                    Aucune action recommandée pour le moment.
                  </div>
                ) : (
                  <ol className="space-y-2">
                    {dash.next_best_actions.map((a, i) => (
                      <li key={a.opportunity_id}
                          data-testid={`ode-action-${i}`}
                          className="flex items-start gap-3 rounded-lg p-2.5"
                          style={{ background: "#FBF6EA",
                                     border: "1px solid #C5A88033" }}>
                        <span className="font-serif text-lg mt-0.5"
                              style={{ color: "#C5A880" }}>#{i + 1}</span>
                        <div className="flex-1">
                          <div className="text-[12px] font-semibold"
                               style={{ color: "#0B2545" }}>{a.title}</div>
                          <div className="text-[11px] mt-0.5"
                               style={{ color: "#334155" }}>{a.next_action}</div>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant={URGENCY_VARIANT[a.urgency]} size="sm">
                              {a.urgency}
                            </Badge>
                            <span className="text-[10px]"
                                  style={{ color: "#94A3B8" }}>
                              {CHANNEL_LABEL[a.channel]} · {money(a.revenue_potential)} · {a.score}/100
                            </span>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ol>
                )}
              </Section>
            </Card>

            {/* Sections en grille */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <SectionCard icon={Sparkles} title="📅 Événements de la semaine"
                           items={dash.events_upcoming} setDetail={setDetail}
                           testid="ode-events" />
              <SectionCard icon={Users} title="🤝 Partenaires à rencontrer"
                           items={dash.partners_to_meet} setDetail={setDetail}
                           testid="ode-partners" />
              <SectionCard icon={MapPin} title="📍 Secteurs territoriaux prioritaires"
                           items={dash.territory_priority} setDetail={setDetail}
                           testid="ode-territory" />
              <SectionCard icon={TrendingUp} title="📈 Nouvelles opportunités détectées"
                           items={dash.new_detected} setDetail={setDetail}
                           testid="ode-new" />
            </div>

            {dash.at_risk.length > 0 && (
              <Card className="p-4 mt-5" testid="ode-atrisk-card"
                    style={{ borderLeft: "3px solid #F59E0B" }}>
                <Section icon={AlertTriangle} title="⚠ Opportunités à risque d'être perdues">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    {dash.at_risk.map((o) => (
                      <OpportunityRow key={o.id} opp={o}
                                        onOpen={() => setDetail(o)} />
                    ))}
                  </div>
                </Section>
              </Card>
            )}
          </>
        )}
      </div>

      {showCreate && (
        <CreateOpportunityModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}
      {detail && (
        <OpportunityDetailModal
          opp={detail}
          onClose={() => setDetail(null)}
          onChanged={() => { setDetail(null); load(); }}
        />
      )}
    </PageShell>
  );
}


// ── Compact row card for an opportunity ────────────────────────────

function OpportunityRow({ opp, onOpen }) {
  return (
    <div onClick={onOpen}
         data-testid={`ode-opp-row-${opp.id}`}
         className="rounded-lg p-3 bg-white cursor-pointer hover:shadow-md transition-shadow"
         style={{ border: "1px solid #C5A88033",
                    borderLeft: `3px solid ${opp.urgency === "critical" ? "#B91C1C"
                                              : opp.urgency === "high" ? "#F59E0B" : "#C5A880"}` }}>
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="text-[13px] font-semibold leading-tight"
             style={{ color: "#0B2545" }}>{opp.title}</div>
        <span className="text-[11px] px-2 py-[1px] rounded-full font-mono"
              style={{ background: "#0B2545", color: "#fff", fontWeight: 700 }}>
          {opp.score}
        </span>
      </div>
      <div className="text-[11px] mb-2" style={{ color: "#94A3B8" }}>
        {KIND_LABEL[opp.kind]} · {opp.city || "—"} · {opp.subtitle || opp.organization || ""}
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <Badge variant={URGENCY_VARIANT[opp.urgency]} size="sm">{opp.urgency}</Badge>
        <span className="text-[10px] font-semibold" style={{ color: "#C5A880" }}>
          {money(opp.revenue_potential)}
        </span>
        <span className="text-[10px]" style={{ color: "#94A3B8" }}>
          {CHANNEL_LABEL[opp.recommended_channel]} · {opp.time_to_convert_days}j
        </span>
      </div>
      {opp.next_action && (
        <div className="text-[11px]" style={{ color: "#334155" }}>
          <ArrowRight size={11} className="inline mr-1" style={{ color: "#C5A880" }} />
          {opp.next_action}
        </div>
      )}
    </div>
  );
}


// ── Section grouping ────────────────────────────────────────────────

function SectionCard({ icon, title, items, setDetail, testid }) {
  return (
    <Card className="p-4" testid={`${testid}-card`}>
      <Section icon={icon} title={title}>
        {items.length === 0 ? (
          <div className="text-[12px] italic py-3" style={{ color: "#94A3B8" }}>
            Rien à signaler pour l'instant.
          </div>
        ) : (
          <div className="space-y-2">
            {items.slice(0, 5).map(o => (
              <OpportunityRow key={o.id} opp={o} onOpen={() => setDetail(o)} />
            ))}
          </div>
        )}
      </Section>
    </Card>
  );
}


// ── Detail modal — the 6 answers ───────────────────────────────────

function OpportunityDetailModal({ opp, onClose, onChanged }) {
  const [products, setProducts] = useState([]);
  const [product, setProduct] = useState(opp.recommended_products?.[0] || "");
  const [premium, setPremium] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [dismissing, setDismissing] = useState(false);

  useEffect(() => {
    api.get("/sales_os/config").then(({ data }) => setProducts(data.products || []));
  }, []);

  const act = async () => {
    if (!product) { toast.error("Choisissez un produit"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/ode/opportunities/${opp.id}/act`, {
        product_key: product,
        premium_annual: parseFloat(premium) || 0,
        notes,
      });
      toast.success("Opportunité convertie en prospect · Sales OS");
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  const dismiss = async () => {
    const reason = window.prompt("Raison (optionnel) :");
    if (reason === null) return;
    setDismissing(true);
    try {
      await api.post(`/ode/opportunities/${opp.id}/dismiss`, { reason });
      toast.success("Opportunité écartée");
      onChanged();
    } finally { setDismissing(false); }
  };

  return (
    <Modal open onClose={onClose} testid="ode-detail-modal" size="lg"
           title={opp.title}
           description={`${KIND_LABEL[opp.kind]} · ${opp.subtitle || opp.organization || opp.city || ""}`}
           footer={
             <>
               <DangerButton onClick={dismiss} disabled={dismissing}
                             testid="ode-detail-dismiss">
                 <X size={13} /> Écarter
               </DangerButton>
               <div className="flex-1" />
               <GhostButton onClick={onClose} testid="ode-detail-close">Fermer</GhostButton>
               <PrimaryButton onClick={act} disabled={busy || opp.status === "acted"}
                              testid="ode-detail-act">
                 <ArrowRight size={13} />
                 {opp.status === "acted" ? "Déjà convertie"
                                          : (busy ? "Conversion…" : "Convertir en prospect")}
               </PrimaryButton>
             </>
           }>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
        <Metric label="Score IA" value={`${opp.score}/100`} testid="ode-detail-score" />
        <Metric label="Adéquation" value={`${opp.fit_score}/100`} testid="ode-detail-fit" />
        <Metric label="Prob. conversion" value={`${opp.conversion_probability}%`}
                testid="ode-detail-prob" />
        <Metric label="Revenu potentiel" value={money(opp.revenue_potential)}
                accent="#C5A880" testid="ode-detail-revenue" />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
        <Metric label="Urgence" value={opp.urgency} testid="ode-detail-urgency" />
        <Metric label="Difficulté" value={`${opp.difficulty}/5`}
                testid="ode-detail-difficulty" />
        <Metric label="Effort" value={`${opp.effort_hours} h`}
                testid="ode-detail-effort" />
        <Metric label="Délai" value={`${opp.time_to_convert_days} j`}
                testid="ode-detail-ttc" />
      </div>

      <Alert variant="info" title="Pourquoi maintenant" testid="ode-detail-reasoning">
        {opp.reasoning || "—"}
      </Alert>

      <div className="mt-3 mb-4">
        <div className="text-[11px] uppercase tracking-widest font-semibold mb-1"
             style={{ color: "#0B2545" }}>Prochaine action</div>
        <div className="text-[12px] rounded-md p-2"
             style={{ background: "#FBF6EA", border: "1px solid #C5A88033",
                        color: "#334155" }}
             data-testid="ode-detail-next-action">
          {opp.next_action}
        </div>
        <div className="text-[10px] mt-1" style={{ color: "#94A3B8" }}>
          Canal recommandé : <strong>{CHANNEL_LABEL[opp.recommended_channel]}</strong>
        </div>
      </div>

      {opp.recommended_products?.length > 0 && (
        <div className="mb-4">
          <div className="text-[11px] uppercase tracking-widest font-semibold mb-1"
               style={{ color: "#0B2545" }}>Produits suggérés</div>
          <div className="flex flex-wrap gap-1"
               data-testid="ode-detail-products">
            {opp.recommended_products.map((k) => (
              <Badge key={k} variant="gold" size="sm">
                {products.find(p => p.key === k)?.label || k}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {opp.status !== "acted" && (
        <div className="rounded-lg p-3" style={{ border: "1px solid #C5A88033",
                                                       background: "#FBF6EA" }}>
          <div className="text-[11px] uppercase tracking-widest font-semibold mb-2"
               style={{ color: "#0B2545" }}>Convertir en prospect Sales OS</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <FormField label="Produit à retenir" required testid="ode-detail-choose-product">
              {({ id }) => (
                <Select id={id} value={product}
                        onChange={(e) => setProduct(e.target.value)}
                        testid="ode-detail-product-select">
                  <option value="">— Choisir —</option>
                  {products.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
                </Select>
              )}
            </FormField>
            <FormField label="Prime annuelle estimée ($)" testid="ode-detail-choose-premium">
              {({ id }) => (
                <Input id={id} type="number" min="0" step="100"
                       value={premium}
                       onChange={(e) => setPremium(e.target.value)}
                       testid="ode-detail-premium-input" />
              )}
            </FormField>
          </div>
          <FormField label="Notes (facultatif)" testid="ode-detail-notes">
            {({ id }) => (
              <Textarea id={id} rows={2} value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        testid="ode-detail-notes-input" />
            )}
          </FormField>
        </div>
      )}
    </Modal>
  );
}


// ── Create modal ────────────────────────────────────────────────────

function CreateOpportunityModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    kind: "ecosystem", title: "", subtitle: "",
    contact_name: "", contact_email: "", contact_phone: "",
    organization: "", city: "Montréal", tags: "",
    event_at: "", description: "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.title.trim()) { toast.error("Un titre est requis"); return; }
    setSaving(true);
    try {
      await api.post("/ode/opportunities", {
        ...form,
        tags: form.tags.split(",").map(t => t.trim()).filter(Boolean),
        event_at: form.event_at ? new Date(form.event_at).toISOString() : null,
      });
      toast.success("Opportunité ajoutée · analysée par l'IA");
      onCreated();
    } catch { toast.error("Erreur"); }
    finally { setSaving(false); }
  };

  return (
    <Modal open onClose={onClose} testid="ode-create-modal"
           title="Ajouter une opportunité"
           description="L'IA calcule le score, le produit, le revenu potentiel et l'action recommandée."
           footer={
             <>
               <GhostButton onClick={onClose} testid="ode-create-cancel">Annuler</GhostButton>
               <PrimaryButton onClick={submit} disabled={saving}
                              testid="ode-create-submit">
                 {saving ? "Analyse…" : "Analyser & sauvegarder"}
               </PrimaryButton>
             </>
           }>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <FormField label="Type d'opportunité" required testid="ode-create-kind">
          {({ id }) => (
            <Select id={id} value={form.kind}
                    onChange={(e) => setForm({ ...form, kind: e.target.value })}
                    testid="ode-create-kind-select">
              {Object.entries(KIND_LABEL).map(([k, l]) => (
                <option key={k} value={k}>{l}</option>
              ))}
            </Select>
          )}
        </FormField>
        <FormField label="Ville" testid="ode-create-city">
          {({ id }) => (
            <Input id={id} value={form.city}
                   onChange={(e) => setForm({ ...form, city: e.target.value })}
                   testid="ode-create-city-input" />
          )}
        </FormField>
      </div>
      <FormField label="Titre" required testid="ode-create-title">
        {({ id }) => (
          <Input id={id} value={form.title}
                 onChange={(e) => setForm({ ...form, title: e.target.value })}
                 placeholder="Ex. Marie Tremblay · échéance REER"
                 testid="ode-create-title-input" />
        )}
      </FormField>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <FormField label="Contact (nom)" testid="ode-create-contact">
          {({ id }) => (
            <Input id={id} value={form.contact_name}
                   onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                   testid="ode-create-contact-input" />
          )}
        </FormField>
        <FormField label="Organisation" testid="ode-create-org">
          {({ id }) => (
            <Input id={id} value={form.organization}
                   onChange={(e) => setForm({ ...form, organization: e.target.value })}
                   testid="ode-create-org-input" />
          )}
        </FormField>
        <FormField label="Courriel" testid="ode-create-email">
          {({ id }) => (
            <Input id={id} type="email" value={form.contact_email}
                   onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                   testid="ode-create-email-input" />
          )}
        </FormField>
        <FormField label="Téléphone" testid="ode-create-phone">
          {({ id }) => (
            <Input id={id} value={form.contact_phone}
                   onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                   testid="ode-create-phone-input" />
          )}
        </FormField>
        <FormField label="Date de l'événement (facultatif)" testid="ode-create-date">
          {({ id }) => (
            <Input id={id} type="datetime-local" value={form.event_at}
                   onChange={(e) => setForm({ ...form, event_at: e.target.value })}
                   testid="ode-create-date-input" />
          )}
        </FormField>
        <FormField label="Tags (séparés par des virgules)" testid="ode-create-tags"
                   description="ex: entrepreneur, famille, reer">
          {({ id }) => (
            <Input id={id} value={form.tags}
                   onChange={(e) => setForm({ ...form, tags: e.target.value })}
                   testid="ode-create-tags-input" />
          )}
        </FormField>
      </div>
      <FormField label="Description / contexte" testid="ode-create-description">
        {({ id }) => (
          <Textarea id={id} rows={3} value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="Ce que vous savez sur cette opportunité."
                    testid="ode-create-description-input" />
        )}
      </FormField>
    </Modal>
  );
}
